import { VueCookieNext } from "vue-cookie-next";

/**
 * Session and identity cookies. Sign-out has to clear all of them: leaving
 * any behind means the next person to sign in on the same browser starts
 * with someone else's entity, institute or role.
 */
export const AUTH_COOKIE_KEYS = [
  "_seip_token",
  "_seip_user",
  "_seip_employee_id",
  "_entity_id",
  "_institute_info_id",
  "_seip_role_id",
  "_seip_entity_type",
  "day_diff",
  "notification_message",
];

export const COOKIE_EXPIRY = "12h";

/**
 * The cookie domain for whichever host is serving the app.
 *
 * The SICIP sites share one session across their subdomains, so they use
 * ".sicip.gov.bd". Everywhere else - localhost, 127.0.0.1, a LAN address,
 * a preview build - an empty value leaves the cookie host-only, which is
 * what the browser accepts and, crucially, what that same host can delete
 * again. Hardcoding a domain is what previously made sign-out fail.
 */
export const cookieDomain = (): string => {
  const host = window.location.hostname;
  return host === "sicip.gov.bd" || host.endsWith(".sicip.gov.bd")
    ? ".sicip.gov.bd"
    : "";
};

/** Cookie options for writing, matched by `clearAuthCookies` on removal. */
export const cookieOptions = (): Record<string, string> => {
  const domain = cookieDomain();
  return domain
    ? { expire: COOKIE_EXPIRY, path: "/", domain }
    : { expire: COOKIE_EXPIRY, path: "/" };
};

/** Applies the host's domain as the default for every later setCookie call. */
export const configureCookies = (): void => {
  VueCookieNext.config({ expire: COOKIE_EXPIRY, domain: cookieDomain() });
};

/**
 * A cookie can only be deleted by a removal whose domain matches the one it
 * was written under. Earlier builds hardcoded "localhost" or "127.0.0.1",
 * so those variants are cleared too - without that, anyone still holding a
 * cookie from an older build could never sign out.
 */
const removalDomains = (): string[] => {
  const candidates = [
    cookieDomain(),
    "",
    window.location.hostname,
    "localhost",
    "127.0.0.1",
  ];
  return candidates.filter((d, i) => candidates.indexOf(d) === i);
};

/** Clears every auth cookie, whichever domain it was written under. */
export const clearAuthCookies = (): void => {
  // Reset the library default first: removeCookie falls back to it when no
  // domain is given, so a stale default would silently re-scope the removal.
  VueCookieNext.config({ expire: COOKIE_EXPIRY, domain: "" });

  removalDomains().forEach((domain) => {
    AUTH_COOKIE_KEYS.forEach((key) => {
      VueCookieNext.removeCookie(
        key,
        domain ? { path: "/", domain } : { path: "/" }
      );
    });
  });

  configureCookies();
};
